// Process Flow Service - CRUD operations for Process Flow Diagrams
// Project-specific, RBAC-aware persistence via Backend API + localStorage fallback
// ============================================================

import { auth } from '../config/firebase';
import { authenticatedFetchJSON } from '../utils/authenticatedFetch';

// Types
export interface SavedProcessFlow {
    id: string;
    projectName: string;
    name: string;
    description?: string;
    createdBy: string;
    createdAt: string;
    updatedBy: string;
    updatedAt: string;
    version: number;
    flow: {
        nodes: any[];
        edges: any[];
        viewport: { x: number; y: number; zoom: number };
    };
    processItems?: any[];
    workElements?: any[]; // Added for persistence
}

export interface ProcessFlowListResponse {
    flows: SavedProcessFlow[];
    total: number;
}

// Storage key for localStorage fallback
const STORAGE_KEY = 'magnus_process_flows';

// ============================================================
// Local Storage Helpers (Fallback when backend unavailable)
// ============================================================

function getLocalFlows(): SavedProcessFlow[] {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (e) {
        console.error('Failed to parse local flows', e);
        return [];
    }
}

function saveLocalFlows(flows: SavedProcessFlow[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(flows));
}

// ============================================================
// CRUD Operations
// ============================================================

/**
 * Get all process flows for a project
 */
export async function getProcessFlows(projectName: string): Promise<SavedProcessFlow[]> {
    // Try backend first
    try {
        const data = await authenticatedFetchJSON<ProcessFlowListResponse>(
            `/api/process-flows?project=${encodeURIComponent(projectName)}`
        );
        return data.flows || [];
    } catch (e) {
        console.warn('Backend unavailable, using localStorage', e);
        // Fallback to localStorage
        return getLocalFlows().filter(f => f.projectName === projectName);
    }
}

/**
 * Get a single process flow by ID
 */
export async function getProcessFlow(id: string): Promise<SavedProcessFlow | null> {
    try {
        return await authenticatedFetchJSON<SavedProcessFlow>(`/api/process-flows/${encodeURIComponent(id)}`);
    } catch (e) {
        console.warn('Backend unavailable, using localStorage', e);
        const flows = getLocalFlows();
        return flows.find(f => f.id === id) || null;
    }
}

/**
 * Create or update a process flow
 */
export async function saveProcessFlow(
    projectName: string,
    name: string,
    flow: { nodes: any[]; edges: any[]; viewport: any },
    processItems?: any[], // Added
    workElements?: any[], // Added
    existingId?: string
): Promise<SavedProcessFlow> {
    const currentUser = auth.currentUser;
    const now = new Date().toISOString();

    const flowData: SavedProcessFlow = {
        id: existingId || `pf-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        projectName,
        name,
        createdBy: currentUser?.email || 'anonymous',
        createdAt: existingId ? '' : now, // Will be set properly on update
        updatedBy: currentUser?.email || 'anonymous',
        updatedAt: now,
        version: 1,
        flow,
        processItems,
        workElements,
    };

    try {
        // Try backend
        if (existingId) {
            return await authenticatedFetchJSON<SavedProcessFlow>(`/api/process-flows/${encodeURIComponent(existingId)}`, {
                method: 'PUT',
                body: JSON.stringify(flowData),
            });
        } else {
            return await authenticatedFetchJSON<SavedProcessFlow>('/api/process-flows', {
                method: 'POST',
                body: JSON.stringify(flowData),
            });
        }
    } catch (e) {
        console.warn('Backend unavailable, using localStorage', e);
        // Fallback to localStorage
        const flows = getLocalFlows();

        if (existingId) {
            const index = flows.findIndex(f => f.id === existingId);
            if (index >= 0) {
                flowData.createdAt = flows[index].createdAt;
                flowData.createdBy = flows[index].createdBy;
                flowData.version = flows[index].version + 1;
                flows[index] = flowData;
            } else {
                flowData.createdAt = now;
                flows.push(flowData);
            }
        } else {
            flowData.createdAt = now;
            flows.push(flowData);
        }

        saveLocalFlows(flows);
        return flowData;
    }
}

/**
 * Delete a process flow
 */
export async function deleteProcessFlow(id: string): Promise<void> {
    try {
        await authenticatedFetchJSON<void>(`/api/process-flows/${encodeURIComponent(id)}`, {
            method: 'DELETE',
        });
    } catch (e) {
        console.warn('Backend unavailable, using localStorage', e);
        const flows = getLocalFlows().filter(f => f.id !== id);
        saveLocalFlows(flows);
    }
}

/**
 * Duplicate a process flow with a new name
 */
export async function duplicateProcessFlow(
    originalId: string,
    newName: string
): Promise<SavedProcessFlow> {
    const original = await getProcessFlow(originalId);
    if (!original) {
        throw new Error('Original flow not found');
    }

    return saveProcessFlow(
        original.projectName,
        newName,
        original.flow
    );
}

// ============================================================
// Export Functions
// ============================================================

/**
 * Export flow as JSON (backup format)
 */
export function exportAsJSON(flow: SavedProcessFlow): string {
    return JSON.stringify(flow, null, 2);
}

/**
 * Export flow as CSV (data table format)
 */
export function exportAsCSV(flow: SavedProcessFlow): string {
    const headers = ['Step Number', 'Label', 'Type', '4M Category', 'Special Char', 'Equipment', 'Cycle Time', 'Control Method', 'Description'];
    const rows = flow.flow.nodes.map((node: any) => {
        const data = node.data || {};
        return [
            data.stepNumber || '',
            data.label || '',
            data.type || '',
            data.element4M || '',
            data.specialCharacteristic || '',
            data.equipment || '',
            data.cycleTime || '',
            data.controlMethod || '',
            (data.description || '').replace(/,/g, ';').replace(/\n/g, ' '),
        ].map(val => `"${val}"`).join(',');
    });

    return [headers.join(','), ...rows].join('\n');
}

/**
 * Export flow diagram as PNG (requires canvas rendering)
 */
export async function exportAsPNG(reactFlowInstance: any): Promise<Blob> {
    // Use html2canvas or react-flow's built-in export
    const dataUrl = await reactFlowInstance.getScreenshot();
    const response = await fetch(dataUrl);
    return response.blob();
}

/**
 * Trigger download of a file
 */
export function downloadFile(content: string | Blob, filename: string, mimeType: string = 'text/plain'): void {
    try {
        // Create blob with proper encoding
        let blob: Blob;
        if (content instanceof Blob) {
            blob = content;
        } else {
            // Use TextEncoder for proper UTF-8 encoding
            const encoder = new TextEncoder();
            const uint8Array = encoder.encode(content);
            blob = new Blob([uint8Array], { type: mimeType + ';charset=utf-8' });
        }

        // Create object URL
        const url = URL.createObjectURL(blob);

        // Create and configure link element
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';

        // Append to body to ensure it works in all browsers
        document.body.appendChild(link);

        // Trigger click
        link.click();

        // Clean up after a delay to ensure download starts
        setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }, 100);

        console.log(`Download initiated: ${filename}`);
    } catch (error) {
        console.error('Download failed:', error);

        // Fallback: open in new tab for user to save manually
        if (typeof content === 'string') {
            const dataUri = `data:${mimeType};charset=utf-8,${encodeURIComponent(content)}`;
            window.open(dataUri, '_blank');
        }
    }
}

// ============================================================
// Import Functions
// ============================================================

/**
 * Import flow from JSON backup
 */
export function importFromJSON(jsonString: string): SavedProcessFlow {
    const parsed = JSON.parse(jsonString);

    // Validate structure
    if (!parsed.flow || !parsed.flow.nodes || !parsed.flow.edges) {
        throw new Error('Invalid flow format: missing nodes or edges');
    }

    // Generate new ID to avoid conflicts
    return {
        ...parsed,
        id: `pf-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        updatedAt: new Date().toISOString(),
    };
}

export default {
    getProcessFlows,
    getProcessFlow,
    saveProcessFlow,
    deleteProcessFlow,
    duplicateProcessFlow,
    exportAsJSON,
    exportAsCSV,
    exportAsPNG,
    downloadFile,
    importFromJSON,
};
